/**
 * AnimatedBrandMark — the SociBaba wordmark as two playful characters.
 *
 * Layout:
 *   [icon] [Soci][Baba][TM] [ suffix? ]
 * Icon and text are perfectly middle-aligned via `inline-flex items-center`
 * with `leading-none` so the text baseline sits at the exact centre of the
 * icon. No layout shift ever — every animation is transform / opacity only,
 * and every letter of Baba lives in a fixed-width slot (min-w-[0.55em]).
 *
 * Fonts (from Google Fonts, imported once in public/index.html):
 *   • Soci: Bricolage Grotesque 600 (semi-bold)
 *   • Baba: Bricolage Grotesque 700 (bold)
 * All other typography on the site is untouched.
 *
 * On-mount sequence (< 2 s total):
 *   1. Icon shakes L-R-L-centre (0.4 s)
 *   2. TM does a proud 360° rotation (0.5 s) — parallel with steps 3+4
 *   3. Soci letters play their individual personalities:
 *      S — snake-stretch (skewX)         O — deep breath (scale)
 *      C — yawn (skewX + rotate)         I — perfectly still
 *   4. Baba letters play their individual personalities:
 *      B1 — high bounce                  A1 — sniff/tilt
 *      B2 — softer follow-up bounce      A2 — vibrate
 *   5. A random mood plays (< 1 s), then the sequence idles.
 *
 * Then every ~9 s a random mood fires (never same one twice in a row):
 *   1. Chase        2. Dig       3. Search     4. Shuffle    5. Heartbeat
 *   6. Wave         7. Glitch    8. MagneticPull  9. Spotlight  10. Whisper
 *
 * The interval pauses when the tab is hidden. There is NO hover trigger.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { COMPANY } from "@/lib/company";
import { useBrandLogos, pickLogo, BrandLogoSlot } from "@/hooks/useBrandLogos";

const MOODS = [
  "chase", "dig", "search", "shuffle", "heartbeat",
  "wave", "glitch", "magnetic", "spotlight", "whisper",
] as const;
type Mood = typeof MOODS[number];

const SHUFFLE_POOL = "!@#$%&*+=?~<>/\\|§¤¥£€";
const TARGET_BABA = ["B", "a", "b", "a"] as const;
const WORDMARK_FONT =
  "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif";

interface Props {
  className?: string;
  markClassName?: string;
  showIcon?: boolean;
  iconClassName?: string;
  variant?: BrandLogoSlot;
  suffix?: string;
  /** `onDark` forces Soci + TM to white on permanently-dark surfaces. */
  tone?: "auto" | "onDark";
  /**
   * `quiet` disables the periodic idle animation entirely — the greeting
   * still runs once on mount, and hover/tap interactions still work, but
   * the 9-second shuffle/glitch cycle is skipped. Use this in dense
   * chrome (sticky headers, top-nav) where mid-animation letter noise
   * looks like a broken UI (e.g. "SociBa##" flash).
   */
  quiet?: boolean;
}

export const AnimatedBrandMark: React.FC<Props> = ({
  className,
  markClassName,
  showIcon = false,
  iconClassName,
  variant = "app",
  suffix,
  tone = "auto",
  quiet = false,
}) => {
  const logos = useBrandLogos();
  const iconSrc = useMemo(() => pickLogo(logos.data, variant), [logos.data, variant]);
  const isCustomIcon = !!logos.data?.[variant];

  // -------------------- Animation controllers --------------------
  const iconC = useAnimation();
  const tmC = useAnimation();
  const sCtl = useAnimation();
  const oCtl = useAnimation();
  const cCtl = useAnimation();
  const iCtl = useAnimation();
  const babaGroupCtl = useAnimation();
  const b1 = useAnimation();
  const a1 = useAnimation();
  const b2 = useAnimation();
  const a2 = useAnimation();
  const babaControls = [b1, a1, b2, a2];

  const [letters, setLetters] = useState<string[]>(() => [...TARGET_BABA]);
  const [glow, setGlow] = useState(false);
  const [dustKey, setDustKey] = useState(0);
  const [showDust, setShowDust] = useState(false);
  const [spotlightKey, setSpotlightKey] = useState(0);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [letterOpacity, setLetterOpacity] = useState<number[]>([1, 1, 1, 1]);
  const [glitchOffsets, setGlitchOffsets] = useState<Array<{ x: number; y: number }>>(
    [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }]
  );
  // Hover-interaction state ---------------------------------------------------
  const [sparklesKey, setSparklesKey] = useState(0);
  const [showSparkles, setShowSparkles] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [rippleKey, setRippleKey] = useState(0);
  const [showRipple, setShowRipple] = useState(false);
  const [showHello, setShowHello] = useState(false);
  const [tmChar, setTmChar] = useState<string>(COMPANY.brandMark);
  const [rainbowActive, setRainbowActive] = useState(false);
  const [sociBlueActive, setSociBlueActive] = useState(false);
  // Particle bursts — id-keyed so multiple overlapping bursts render cleanly
  // and are torn down after their animation ends (no memory leak).
  type BurstKind = "sparks" | "shimmer" | "fire" | "ring" | "confetti";
  interface Burst { id: number; kind: BurstKind; count: number; }
  const [bursts, setBursts] = useState<Burst[]>([]);
  const burstIdRef = useRef(0);
  // prefers-reduced-motion → skip particles entirely (text moods still play).
  const [reduceMotion, setReduceMotion] = useState(false);
  // Battery-saver on mobile → drop particle count by ~70 %.
  const [lowPower, setLowPower] = useState(false);
  // First-visit "try clicking me" whisper — persists via localStorage so a
  // returning user never sees it again. Fires only after they linger ~900 ms.
  const NUDGE_KEY = "sb_brand_nudge_seen_v1";
  const [showNudge, setShowNudge] = useState(false);
  const nudgeShownRef = useRef(false);
  const nudgeTimerRef = useRef<any>(null);

  const busyRef = useRef(false);
  // Set to false on unmount; every animation runner bails out immediately if
  // the component is no longer mounted, and every controls.start() call is
  // wrapped in a try/catch so framer's "must be mounted" invariant can never
  // surface to the user as a runtime error (fires during React strict-mode
  // double-mounts or when the route changes mid-sequence).
  const mountedRef = useRef(true);
  // Each hover target owns its own busy-ref so a mouseenter on the outer
  // wrapper never starves the more specific inner target's handler.
  const iconBusy = useRef(false);
  const sociBusy = useRef(false);
  const babaBusy = useRef(false);
  const tmBusy = useRef(false);
  const fullBusy = useRef(false);
  const lastMoodRef = useRef<Mood | null>(null);
  const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const canHover = () =>
    typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)").matches;
  const pickOne = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

  // ---------- Icon head-shake (0.4 s) + TM 360° (0.5 s) ----------
  const runIconAndTm = useCallback(async () => {
    // Icon: left → right → left → centre
    await iconC.start({
      rotate: [0, -15, 15, -10, 0],
      transition: { duration: 0.4, times: [0, 0.25, 0.55, 0.8, 1], ease: "easeInOut" },
    });
    // TM: proud full rotation, kicks off as icon settles.
    tmC.start({
      rotate: [0, 360],
      transition: { duration: 0.55, ease: "easeInOut" },
    });
  }, [iconC, tmC]);

  // ---------- Per-letter Soci personalities (parallel) ----------
  const runSociPersonalities = useCallback(async () => {
    await Promise.all([
      sCtl.start({
        // S — snake stretch
        scaleX: [1, 1.18, 0.94, 1],
        skewX: ["0deg", "12deg", "-6deg", "0deg"],
        transition: { duration: 0.6, ease: "easeInOut" },
      }),
      oCtl.start({
        // O — deep breath
        scale: [1, 1.3, 1],
        transition: { duration: 0.6, ease: "easeInOut", delay: 0.05 },
      }),
      cCtl.start({
        // C — yawn
        scaleX: [1, 1.2, 1],
        rotate: [0, -8, 0],
        transition: { duration: 0.55, ease: "easeInOut", delay: 0.1 },
      }),
      // I stays perfectly still — represents intelligence and stability
      iCtl.start({ scale: 1, rotate: 0, transition: { duration: 0.4 } }),
    ]);
  }, [sCtl, oCtl, cCtl, iCtl]);

  // ---------- Per-letter Baba personalities (parallel) ----------
  const runBabaPersonalities = useCallback(async () => {
    await Promise.all([
      b1.start({ y: [0, -14, 2, -3, 0], transition: { duration: 0.55, ease: "easeOut" } }),
      a1.start({
        rotate: [0, 12, -12, 8, 0],
        transition: { duration: 0.55, ease: "easeInOut", delay: 0.05 },
      }),
      b2.start({ y: [0, -7, 1, 0], transition: { duration: 0.55, ease: "easeOut", delay: 0.1 } }),
      a2.start({
        x: [0, -3, 3, -3, 3, 0],
        transition: { duration: 0.5, ease: "linear", delay: 0.05 },
      }),
    ]);
  }, [b1, a1, b2, a2]);

  // -------------------- Mood implementations --------------------
  const pickMood = (): Mood => {
    const pool = MOODS.filter((m) => m !== lastMoodRef.current);
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const runMood = useCallback(
    async (forced?: Mood) => {
      const m = forced || pickMood();
      lastMoodRef.current = m;

      if (m === "chase") {
        await Promise.all([
          babaGroupCtl.start({
            x: [0, 4, -1, 0],
            transition: { duration: 0.9, times: [0, 0.35, 0.7, 1] },
          }),
          Promise.all(
            babaControls.map((c, i) =>
              c.start({
                y: [0, -6 + i * 1.5, 0],
                transition: { duration: 0.9, delay: i * 0.05 },
              })
            )
          ),
        ]);
      } else if (m === "dig") {
        setDustKey((k) => k + 1);
        setShowDust(true);
        await Promise.all(
          babaControls.map((c) =>
            c.start({
              x: [0, -2, 2, -2, 2, -2, 2, 0],
              transition: { duration: 0.8, ease: "linear" },
            })
          )
        );
        setTimeout(() => setShowDust(false), 350);
        await babaGroupCtl.start({
          y: [0, -3, 0],
          transition: { duration: 0.25, ease: "easeOut" },
        });
      } else if (m === "search") {
        const scan = [-3, 3, -3, 3, 0];
        for (const off of scan) {
          await Promise.all(
            babaControls.map((c, i) =>
              c.start({
                x: off * (i % 2 === 0 ? 1 : -1),
                transition: { duration: 0.12 },
              })
            )
          );
        }
        setGlow(true);
        await wait(320);
        setGlow(false);
      } else if (m === "shuffle") {
        for (let i = 0; i < TARGET_BABA.length; i++) {
          for (let k = 0; k < 4; k++) {
            const next = TARGET_BABA.map((c, j) =>
              j < i ? c : SHUFFLE_POOL[Math.floor(Math.random() * SHUFFLE_POOL.length)]
            );
            setLetters(next);
            await wait(40);
          }
        }
        setLetters([...TARGET_BABA]);
      } else if (m === "heartbeat") {
        await babaGroupCtl.start({
          scale: [1, 1.14, 0.98, 1.14, 1],
          transition: { duration: 0.75, times: [0, 0.2, 0.45, 0.65, 1], ease: "easeOut" },
        });
      } else if (m === "wave") {
        // Ripple: each letter briefly hops upward in sequence.
        for (let i = 0; i < babaControls.length; i++) {
          babaControls[i].start({
            y: [0, -8, 0],
            transition: { duration: 0.4, ease: "easeOut" },
          });
          await wait(90);
        }
        await wait(280);
      } else if (m === "glitch") {
        // Digital split — 4 quick offset frames then snap back.
        for (let k = 0; k < 4; k++) {
          setGlitchOffsets(
            TARGET_BABA.map(() => ({
              x: (Math.random() - 0.5) * 5,
              y: (Math.random() - 0.5) * 3,
            }))
          );
          await wait(55);
        }
        setGlitchOffsets([{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }]);
        await wait(120);
      } else if (m === "magnetic") {
        // Soci and Baba compress together then spring apart.
        await Promise.all([
          sCtl.start({
            x: [0, 3, -1, 0],
            transition: { duration: 0.7, times: [0, 0.4, 0.75, 1], ease: "easeInOut" },
          }),
          oCtl.start({
            x: [0, 3, -1, 0],
            transition: { duration: 0.7, times: [0, 0.4, 0.75, 1], ease: "easeInOut" },
          }),
          cCtl.start({
            x: [0, 3, -1, 0],
            transition: { duration: 0.7, times: [0, 0.4, 0.75, 1], ease: "easeInOut" },
          }),
          iCtl.start({
            x: [0, 3, -1, 0],
            transition: { duration: 0.7, times: [0, 0.4, 0.75, 1], ease: "easeInOut" },
          }),
          babaGroupCtl.start({
            x: [0, -3, 1, 0],
            scaleX: [1, 0.94, 1.03, 1],
            transition: { duration: 0.7, times: [0, 0.4, 0.75, 1], ease: "easeInOut" },
          }),
        ]);
      } else if (m === "spotlight") {
        setSpotlightKey((k) => k + 1);
        setShowSpotlight(true);
        await wait(900);
        setShowSpotlight(false);
      } else if (m === "whisper") {
        // Fade each letter to ~15% opacity then brighten back one by one.
        for (let i = 0; i < TARGET_BABA.length; i++) {
          setLetterOpacity((prev) => prev.map((v, j) => (j === i ? 0.15 : v)));
          await wait(80);
        }
        for (let i = 0; i < TARGET_BABA.length; i++) {
          setLetterOpacity((prev) => prev.map((v, j) => (j === i ? 1 : v)));
          await wait(90);
        }
      }

      // Reset shared controls so the next mood starts from a clean state.
      await Promise.all([
        babaGroupCtl.start({ x: 0, y: 0, scale: 1, scaleX: 1, transition: { duration: 0.15 } }),
        ...babaControls.map((c) =>
          c.start({ x: 0, y: 0, rotate: 0, transition: { duration: 0.15 } })
        ),
        sCtl.start({ x: 0 }),
        oCtl.start({ x: 0 }),
        cCtl.start({ x: 0 }),
        iCtl.start({ x: 0 }),
      ]);
    },
    [babaGroupCtl, babaControls, sCtl, oCtl, cCtl, iCtl]
  );

  // ---------- Full on-mount greeting sequence ----------
  const runGreeting = useCallback(async () => {
    if (busyRef.current || !mountedRef.current) return;
    busyRef.current = true;
    try {
      await runIconAndTm();
      if (!mountedRef.current) return;
      // steps 3 + 4 in parallel with the tail of the TM rotation
      await Promise.all([runSociPersonalities(), runBabaPersonalities()]);
      if (!mountedRef.current) return;
      await runMood();
    } catch {
      /* framer's mounted-invariant can fire if the component unmounts
         between an `await` and the next controls.start() — swallow silently. */
    } finally {
      busyRef.current = false;
    }
  }, [runIconAndTm, runSociPersonalities, runBabaPersonalities, runMood]);

  // ---------- Periodic mood-only rotation ----------
  const runPeriodicMood = useCallback(async () => {
    if (busyRef.current || !mountedRef.current) return;
    busyRef.current = true;
    try {
      await runMood();
    } catch {
      /* same reason as above */
    } finally {
      busyRef.current = false;
    }
  }, [runMood]);

  useEffect(() => {
    mountedRef.current = true;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        nudgeShownRef.current = window.localStorage.getItem(NUDGE_KEY) === "1";
      }
      // Reduced-motion opt-out — respected everywhere.
      if (typeof window !== "undefined" && window.matchMedia) {
        setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
      }
      // Battery-saver detection (Chrome / Edge mobile). Firefox / Safari
      // don't expose this — we default to false (full particles).
      const nav: any = typeof navigator !== "undefined" ? navigator : null;
      if (nav && typeof nav.getBattery === "function") {
        nav.getBattery().then((b: any) => {
          if (mountedRef.current) setLowPower(!!b && (b.level < 0.2 || b.charging === false && b.level < 0.3));
        }).catch(() => {});
      }
    } catch { /* ignore storage / matchMedia errors — safe defaults */ }
    const t = setTimeout(() => runGreeting(), 350);
    let iv: any = null;
    const start = () => {
      if (iv) return;
      // In `quiet` mode (used inside dense chrome like the sticky Shop header)
      // we skip the 9-second periodic mood cycle entirely — the greeting on
      // mount still plays and hover/tap interactions still work, but the
      // shuffle / glitch flashes that resemble truncation ("SociBa##") are
      // silenced.
      if (quiet) return;
      iv = setInterval(() => runPeriodicMood(), 9000);
    };
    const stop = () => {
      if (iv) { clearInterval(iv); iv = null; }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      mountedRef.current = false;
      clearTimeout(t);
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [runGreeting, runPeriodicMood, quiet]);

  // -------------------- Hover interactions (desktop only) --------------------
  // Each target picks a random micro-animation from its pool and runs it with
  // a single `hoverBusyRef` so multiple triggers can't stack. Hover work
  // deliberately overrides in-flight auto-play — auto-play tolerates being
  // pre-empted because its reset step re-baselines shared controllers.
  const withHover = useCallback(async (ref: React.MutableRefObject<boolean>, fn: () => Promise<void>) => {
    if (!canHover() || ref.current || !mountedRef.current) return;
    ref.current = true;
    try { await fn(); } catch { /* framer late-mount invariant — swallow */ } finally { ref.current = false; }
  }, []);

  const onIconHover = () => { spawnBurst("sparks"); withHover(iconBusy, async () => {
    const k = pickOne(["wink", "tilt", "nod", "spin", "grow", "laugh", "sparkle"]);
    if (k === "wink") {
      await iconC.start({ scaleY: [1, 0.12, 1], transition: { duration: 0.35, times: [0, 0.5, 1], ease: "easeInOut" } });
    } else if (k === "tilt") {
      await iconC.start({ rotate: [0, -18, -14, -18, 0], transition: { duration: 0.7, ease: "easeInOut" } });
    } else if (k === "nod") {
      await iconC.start({ scaleY: [1, 0.86, 1, 0.9, 1], transition: { duration: 0.75, ease: "easeOut" } });
    } else if (k === "spin") {
      await iconC.start({ rotate: [0, 360], transition: { duration: 0.6, ease: "easeInOut" } });
      iconC.set({ rotate: 0 });
    } else if (k === "grow") {
      await iconC.start({ scale: [1, 1.35, 0.95, 1.08, 1], transition: { duration: 0.6, ease: "easeOut" } });
    } else if (k === "laugh") {
      await iconC.start({ rotate: [0, -6, 6, -6, 6, -4, 4, 0], transition: { duration: 0.55, ease: "linear" } });
    } else if (k === "sparkle") {
      setSparklesKey((v) => v + 1); setShowSparkles(true);
      await wait(700); setShowSparkles(false);
    }
  }); };

  const onSociHover = () => { spawnBurst("shimmer"); withHover(sociBusy, async () => {
    const k = pickOne(["rise", "question", "individual", "blue", "scatter", "grow"]);
    if (k === "rise") {
      const arr = [sCtl, oCtl, cCtl, iCtl];
      for (let i = 0; i < arr.length; i++) {
        arr[i].start({ y: [0, -8, 0], transition: { duration: 0.5, ease: "easeOut" } });
        await wait(90);
      }
    } else if (k === "question") {
      // Bounce all letters up so the wordmark "flips itself" into a mystery
      // — cheaper than swapping DOM nodes and works with fixed-width letters.
      await Promise.all([sCtl, oCtl, cCtl, iCtl].map((c) =>
        c.start({ y: [0, -12, 0], rotate: [0, 8, 0], transition: { duration: 0.55 } })
      ));
    } else if (k === "individual") {
      // S slithers left, O rolls, C spins, I stays still — simultaneously.
      await Promise.all([
        sCtl.start({ x: [0, -8, 0], skewX: ["0deg", "20deg", "0deg"], transition: { duration: 0.6 } }),
        oCtl.start({ rotate: [0, 360], transition: { duration: 0.6 } }),
        cCtl.start({ rotate: [0, -180, 0], transition: { duration: 0.6 } }),
      ]);
      oCtl.set({ rotate: 0 });
    } else if (k === "blue") {
      setSociBlueActive(true);
      await wait(650);
      setSociBlueActive(false);
    } else if (k === "scatter") {
      await Promise.all([
        sCtl.start({ x: [0, -6, 0], transition: { duration: 0.5 } }),
        oCtl.start({ x: [0, -2, 0], transition: { duration: 0.5 } }),
        cCtl.start({ x: [0, 2, 0], transition: { duration: 0.5 } }),
        iCtl.start({ x: [0, 6, 0], transition: { duration: 0.5 } }),
      ]);
    } else if (k === "grow") {
      await Promise.all([sCtl, oCtl, cCtl, iCtl].map((c) =>
        c.start({ scale: [1, 1.6, 1], transition: { duration: 0.55, ease: "easeOut" } })
      ));
    }
  }); };

  const onBabaHover = () => { spawnBurst("fire"); withHover(babaBusy, async () => {
    const k = pickOne(["erupt", "flip", "chaos", "glow", "swap", "shatter", "growseq"]);
    if (k === "erupt") {
      const dirs = [[-14, -6], [14, -8], [-10, 10], [12, 8]];
      await Promise.all(babaControls.map((c, i) =>
        c.start({ x: [0, dirs[i][0], 0], y: [0, dirs[i][1], 0], transition: { duration: 0.7, times: [0, 0.5, 1], ease: "easeOut" } })
      ));
    } else if (k === "flip") {
      await babaGroupCtl.start({ rotateY: [0, 180, 360], transition: { duration: 0.9, ease: "easeInOut" } });
      babaGroupCtl.set({ rotateY: 0 });
    } else if (k === "chaos") {
      const heights = [-16, -8, -20, -12];
      await Promise.all(babaControls.map((c, i) =>
        c.start({ y: [0, heights[i], 0], transition: { duration: 0.6, ease: "easeOut" } })
      ));
    } else if (k === "glow") {
      setGlow(true); await wait(900); setGlow(false);
    } else if (k === "swap") {
      setLetters(["A", "B", "A", "B"]); await wait(500); setLetters([...TARGET_BABA]);
    } else if (k === "shatter") {
      await Promise.all(babaControls.map((c, i) => {
        const dx = [-14, 12, -10, 16][i]; const dy = [-8, 10, 12, -6][i];
        return c.start({
          x: [0, dx, 0], y: [0, dy, 0], rotate: [0, i % 2 ? 20 : -20, 0],
          transition: { duration: 0.7 },
        });
      }));
    } else if (k === "growseq") {
      for (let i = 0; i < babaControls.length; i++) {
        babaControls[i].start({ scale: [1, 1.4, 1], transition: { duration: 0.35 } });
        await wait(90);
      }
    }
  }); };

  const onTMHover = () => { spawnBurst("ring"); withHover(tmBusy, async () => {
    const k = pickOne(["triple", "size", "crown", "ripple", "confetti"]);
    if (k === "triple") {
      await tmC.start({ rotate: [0, 1080], transition: { duration: 0.8, ease: "easeInOut" } });
      await tmC.start({ scale: [1, 1.2, 1], transition: { duration: 0.25 } });
      tmC.set({ rotate: 0 });
    } else if (k === "size") {
      await tmC.start({ scale: [1, 3, 1], transition: { duration: 0.7, ease: "easeOut" } });
    } else if (k === "crown") {
      setTmChar("👑"); await wait(700); setTmChar(COMPANY.brandMark);
    } else if (k === "ripple") {
      setRippleKey((v) => v + 1); setShowRipple(true);
      await wait(900); setShowRipple(false);
    } else if (k === "confetti") {
      setConfettiKey((v) => v + 1); setShowConfetti(true);
      await wait(900); setShowConfetti(false);
    }
  }); };

  const onFullHover = () => { spawnBurst("confetti"); withHover(fullBusy, async () => {
    const k = pickOne(["bow", "tilt3d", "rainbow", "explode", "hello"]);
    if (k === "bow") {
      // Small synchronised nod — soci letters + baba group tilt forward together.
      await Promise.all([
        sCtl.start({ y: [0, 4, 0], transition: { duration: 0.55, ease: "easeInOut" } }),
        oCtl.start({ y: [0, 4, 0], transition: { duration: 0.55, ease: "easeInOut" } }),
        cCtl.start({ y: [0, 4, 0], transition: { duration: 0.55, ease: "easeInOut" } }),
        iCtl.start({ y: [0, 4, 0], transition: { duration: 0.55, ease: "easeInOut" } }),
        babaGroupCtl.start({ y: [0, 4, 0], scaleY: [1, 0.92, 1], transition: { duration: 0.55, ease: "easeInOut" } }),
      ]);
    } else if (k === "tilt3d") {
      await babaGroupCtl.start({ rotateX: [0, -12, 0], rotateY: [0, 12, 0], transition: { duration: 0.7 } });
      babaGroupCtl.set({ rotateX: 0, rotateY: 0 });
    } else if (k === "rainbow") {
      setRainbowActive(true); await wait(900); setRainbowActive(false);
    } else if (k === "explode") {
      await wait(400);
      await Promise.all(babaControls.map((c, i) => {
        const dx = [-16, 14, -12, 16][i];
        return c.start({ x: [0, dx, 0], transition: { duration: 0.55, ease: "easeOut" } });
      }));
    } else if (k === "hello") {
      setShowHello(true); await wait(1100); setShowHello(false);
    }
  }); };

  const resolvedIconClass = useMemo(() => {
    const base =
      iconClassName ||
      "h-[1.25em] w-[1.25em] inline-block align-middle shrink-0";
    // Admin-uploaded icons keep their intended look; only the packaged default
    // gets dark-mode inversion. If the caller already supplies a dark:invert
    // class, strip it when a custom icon is in use.
    if (isCustomIcon) {
      return base.replace(/(^|\s)dark:invert(\s|$)/g, " ").trim();
    }
    // If the caller didn't ask for invert, add it for the default icon.
    return /dark:invert/.test(base) ? base : `${base} dark:invert`;
  }, [iconClassName, isCustomIcon]);

  const sociTextColor =
    tone === "onDark"
      ? "text-white"
      : "text-[color:var(--sg-fg,#0F172A)] dark:text-white";

  // Whisper trigger: fires the nudge once the visitor has lingered on the
  // wrapper for ~900 ms. Guarded by nudgeShownRef so it's a true one-time cue.
  const armNudge = () => {
    if (nudgeShownRef.current || !canHover()) return;
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setShowNudge(true);
      nudgeShownRef.current = true;
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(NUDGE_KEY, "1");
        }
      } catch { /* ignore */ }
      setTimeout(() => { if (mountedRef.current) setShowNudge(false); }, 3400);
    }, 900);
  };
  const disarmNudge = () => {
    if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
  };

  // Spawn a particle burst. Auto-removes after its animation completes so
  // no DOM stays alive past its useful life. Skipped entirely under
  // prefers-reduced-motion. Battery-saver knocks count down by ~70 %.
  const BURST_TTL: Record<BurstKind, number> = {
    sparks: 900, shimmer: 1100, fire: 1200, ring: 950, confetti: 1700,
  };
  const BURST_BASE: Record<BurstKind, number> = {
    sparks: 14, shimmer: 10, fire: 12, ring: 1, confetti: 20,
  };
  const spawnBurst = (kind: BurstKind) => {
    if (reduceMotion || !mountedRef.current) return;
    const scale = lowPower ? 0.3 : 1;
    const count = Math.max(1, Math.round(BURST_BASE[kind] * scale));
    const id = ++burstIdRef.current;
    setBursts((b) => [...b, { id, kind, count }]);
    setTimeout(() => {
      if (mountedRef.current) setBursts((b) => b.filter((x) => x.id !== id));
    }, BURST_TTL[kind] + 100);
  };

  return (
    <span
      className={`${className || ""} relative inline-flex items-center gap-1.5 select-none leading-none`}
      style={{ fontFamily: WORDMARK_FONT, isolation: "isolate" }}
      data-testid="animated-brand-mark"
      onMouseEnter={() => { armNudge(); onFullHover(); }}
      onMouseLeave={disarmNudge}
      onTouchStart={() => { spawnBurst("confetti"); }}
    >
      {showIcon && (
        <span className="relative inline-flex items-center leading-none cursor-pointer" onMouseEnter={onIconHover} onTouchStart={() => spawnBurst("sparks")}>
          <motion.img
            src={iconSrc}
            alt=""
            aria-hidden="true"
            className={resolvedIconClass}
            draggable={false}
            animate={iconC}
            style={{ transformOrigin: "50% 55%" }}
            data-testid="brand-mark-icon"
          />
          {/* Sparkles burst around the icon on hover H7. */}
          {showSparkles && (
            <span key={sparklesKey} aria-hidden="true" className="absolute inset-[-8px] pointer-events-none">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 1, x: 0, y: 0, scale: 0 }}
                  animate={{
                    opacity: [1, 0],
                    x: Math.cos((i / 6) * Math.PI * 2) * 22,
                    y: Math.sin((i / 6) * Math.PI * 2) * 22,
                    scale: [0.4, 1.4, 0],
                  }}
                  transition={{ duration: 0.7, delay: i * 0.03, ease: "easeOut" }}
                  className="absolute left-1/2 top-1/2 -ml-[3px] -mt-[3px] h-1.5 w-1.5 rounded-full bg-yellow-300"
                  style={{ boxShadow: "0 0 6px 2px rgba(251,191,36,0.75)" }}
                />
              ))}
            </span>
          )}
        </span>
      )}
      <span
        className="inline-flex items-center leading-none"
        style={{ fontFamily: WORDMARK_FONT }}
      >
        {/* Soci — semi-bold, calm colour */}
        <span
          className={`inline-flex items-center leading-none cursor-pointer ${
            sociBlueActive ? "text-[#3b82f6]" : sociTextColor
          }`}
          style={{ fontWeight: 600, fontFamily: WORDMARK_FONT, transition: "color 200ms ease-out" }}
          onMouseEnter={onSociHover}
          onTouchStart={() => spawnBurst("shimmer")}
          data-testid="soci-group"
        >
          <motion.span animate={sCtl} className="inline-block will-change-transform origin-bottom" data-testid="soci-letter-s">S</motion.span>
          <motion.span animate={oCtl} className="inline-block will-change-transform" data-testid="soci-letter-o">o</motion.span>
          <motion.span animate={cCtl} className="inline-block will-change-transform origin-center" data-testid="soci-letter-c">c</motion.span>
          <motion.span animate={iCtl} className="inline-block will-change-transform" data-testid="soci-letter-i">i</motion.span>
        </span>
        {/* Baba — bold, orange→gold gradient, letter-level personalities.
            Note: the gradient + bg-clip-text lives on each LETTER, not the
            parent, because `will-change: transform` promotes each letter to
            its own paint layer which would otherwise render the clipped
            gradient as invisible transparent text. */}
        <motion.span
          animate={babaGroupCtl}
          onMouseEnter={onBabaHover}
          onTouchStart={() => spawnBurst("fire")}
          data-testid="baba-group"
          className="relative inline-flex items-center leading-none will-change-transform cursor-pointer animate-[emberBreath_3.6s_ease-in-out_infinite]"
          style={{
            fontWeight: 700,
            fontFamily: WORDMARK_FONT,
            transformStyle: "preserve-3d",
            perspective: 600,
            // Contain layout so even wider-than-average shuffle glyphs cannot
            // push adjacent elements (nav items to the right of the logo).
            contain: "layout paint",
            filter: glow
              ? "drop-shadow(0 0 6px rgba(251,146,60,0.75)) drop-shadow(0 0 10px rgba(251,191,36,0.35))"
              : undefined,
            transition: "filter 220ms ease-out",
          }}
        >
          {letters.map((ch, i) => (
            <motion.span
              key={i}
              animate={babaControls[i]}
              className={`inline-block text-center overflow-hidden will-change-transform w-[0.62em] ${
                rainbowActive
                  ? "bg-gradient-to-r from-red-500 via-purple-500 via-blue-500 to-emerald-400 bg-clip-text text-transparent"
                  : "bg-gradient-to-r from-[#f97316] via-[#fb923c] to-[#fbbf24] bg-clip-text text-transparent"
              }`}
              style={{
                opacity: letterOpacity[i],
                transform: `translate(${glitchOffsets[i].x}px, ${glitchOffsets[i].y}px)`,
                transition: "opacity 120ms ease-out",
              }}
              data-testid={`baba-letter-${i}`}
            >
              {ch}
            </motion.span>
          ))}
          {/* Dust puffs — anchored absolutely so they never affect layout. */}
          <span
            key={dustKey}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-[2px] pointer-events-none flex gap-[3px]"
            aria-hidden="true"
          >
            {showDust &&
              [0, 1, 2, 3].map((i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0.85, y: -2, scale: 0.7 }}
                  animate={{ opacity: 0, y: 7, scale: 1.6 }}
                  transition={{ duration: 0.55, delay: i * 0.05, ease: "easeOut" }}
                  className="block h-[3px] w-[3px] rounded-full bg-[#fbbf24]"
                />
              ))}
          </span>
          {/* Spotlight — a gold shimmer sweeping across Baba. */}
          {showSpotlight && (
            <motion.span
              key={spotlightKey}
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none rounded-sm"
              initial={{ x: "-120%", opacity: 0 }}
              animate={{ x: "120%", opacity: [0, 0.75, 0] }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.55) 45%, rgba(255,255,255,0.75) 50%, rgba(251,191,36,0.55) 55%, transparent 100%)",
                mixBlendMode: "screen",
                filter: "blur(2px)",
              }}
            />
          )}
        </motion.span>
        {/* Trademark — matches Soci colour, does a proud 360° after icon settles.
            The wrapper carries transparent padding so pointer hovers reliably
            land even though the <sup> itself is tiny. */}
        <span
          className="relative inline-flex items-center px-1 py-1 -mx-1 -my-1 cursor-pointer"
          onMouseEnter={onTMHover}
          onTouchStart={() => spawnBurst("ring")}
          data-testid="tm-wrapper"
        >
          <motion.sup
            animate={tmC}
            data-testid="brand-tm"
            aria-label="trademark"
            className={`${markClassName || "text-[0.5em] align-super ml-[1px] opacity-70 font-normal"} inline-block will-change-transform ${sociTextColor}`}
            style={{ transformOrigin: "50% 60%" }}
          >
            {tmChar}
          </motion.sup>
          {/* TM ripple ring (H24) */}
          {showRipple && (
            <motion.span
              key={rippleKey}
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full"
              initial={{ width: 4, height: 4, opacity: 0.85 }}
              animate={{ width: 36, height: 36, opacity: 0 }}
              transition={{ duration: 0.85, ease: "easeOut" }}
              style={{ border: "2px solid rgba(251,191,36,0.75)" }}
            />
          )}
          {/* TM confetti (H25) */}
          {showConfetti && (
            <span key={confettiKey} aria-hidden="true" className="absolute inset-[-10px] pointer-events-none">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
                const colors = ["#f97316", "#fbbf24", "#22c55e", "#3b82f6", "#a855f7"];
                return (
                  <motion.span
                    key={i}
                    initial={{ opacity: 1, x: 0, y: 0, scale: 0 }}
                    animate={{
                      opacity: [1, 0],
                      x: Math.cos((i / 8) * Math.PI * 2) * 28,
                      y: Math.sin((i / 8) * Math.PI * 2) * 28 - 6,
                      scale: [0.3, 1.2, 0.6],
                      rotate: [0, 360],
                    }}
                    transition={{ duration: 0.85, delay: i * 0.02, ease: "easeOut" }}
                    className="absolute left-1/2 top-1/2 h-1.5 w-2 -ml-[4px] -mt-[3px] rounded-sm"
                    style={{ backgroundColor: colors[i % colors.length] }}
                  />
                );
              })}
            </span>
          )}
        </span>
      </span>
      {suffix ? (
        <span
          className="inline-flex items-center leading-none ml-1 font-semibold text-[color:var(--sb-saffron,#f97316)] text-[0.72em] tracking-wide"
          style={{ fontFamily: WORDMARK_FONT, fontWeight: 700 }}
        >
          {suffix}
        </span>
      ) : null}
      {/* "Hello!" greeting bubble on full-logo hover (H30) */}
      {showHello && (
        <motion.span
          aria-hidden="true"
          initial={{ opacity: 0, y: 6, scale: 0.9 }}
          animate={{ opacity: [0, 1, 1, 0], y: [6, -4, -4, -8], scale: [0.9, 1, 1, 1] }}
          transition={{ duration: 1.1, times: [0, 0.2, 0.75, 1], ease: "easeOut" }}
          className="absolute -top-6 left-6 pointer-events-none text-[color:var(--sb-saffron,#f97316)]"
          style={{
            fontFamily: "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif",
            fontWeight: 700,
            fontStyle: "italic",
            fontSize: "0.85em",
          }}
        >
          Hello!
        </motion.span>
      )}
      {/* First-visit discovery nudge — a subtle whisper hinting the logo is
          interactive. Positioned absolutely so it never affects surrounding
          layout, pointer-events-none so it can't intercept hovers. */}
      {showNudge && (
        <motion.span
          aria-hidden="true"
          data-testid="brand-mark-nudge"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: [0, 1, 1, 0], y: [4, -2, -2, -6] }}
          transition={{ duration: 3.4, times: [0, 0.12, 0.85, 1], ease: "easeOut" }}
          className="absolute -bottom-5 left-1 pointer-events-none whitespace-nowrap text-[10px] italic font-medium"
          style={{
            color: "color-mix(in srgb, var(--sg-muted, #64748B) 90%, transparent)",
            fontFamily: "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif",
          }}
        >
          try clicking me ✨
        </motion.span>
      )}
      {/* Particle overlay — spawns per-target contextual bursts. Each burst
          auto-tears down via a setTimeout in spawnBurst so DOM stays clean.
          Skipped entirely when prefers-reduced-motion is set. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-visible"
        data-testid="brand-mark-particles"
        style={{ zIndex: 5 }}
      >
        {bursts.map((b) => (
          <BurstOverlay key={b.id} kind={b.kind} count={b.count} />
        ))}
      </span>
    </span>
  );
};

/**
 * BurstOverlay — pure-transform particle burst. Uses framer-motion for
 * GPU-accelerated animation; each particle self-destructs when its parent
 * <BurstOverlay> unmounts (parent controls lifetime via spawnBurst's
 * setTimeout). No DOM leak.
 */
const BurstOverlay: React.FC<{ kind: "sparks" | "shimmer" | "fire" | "ring" | "confetti"; count: number }> = ({ kind, count }) => {
  const items = Array.from({ length: count }, (_, i) => i);
  if (kind === "ring") {
    // Single expanding gold ring, positioned near the TM area (right side).
    return (
      <motion.span
        aria-hidden="true"
        initial={{ opacity: 0.9, scale: 0.2 }}
        animate={{ opacity: 0, scale: 1.6 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full pointer-events-none"
        style={{
          width: 40, height: 40, border: "2px solid rgba(251,191,36,0.85)",
          boxShadow: "0 0 12px rgba(251,191,36,0.55)",
        }}
      />
    );
  }
  // Anchor points (approx, relative to the logo wrapper) for each burst kind.
  const anchor = kind === "sparks" ? { left: 12, top: "50%" }
              : kind === "shimmer" ? { left: 40, top: "50%" }
              : kind === "fire" ? { left: "60%", top: "50%" }
              : { left: "50%", top: "50%" };
  return (
    <>
      {items.map((i) => {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
        const dist = 18 + Math.random() * 22;
        let x = Math.cos(angle) * dist;
        let y = Math.sin(angle) * dist;
        let bg = "#fbbf24"; let sz = 3;
        if (kind === "sparks") { bg = ["#fbbf24", "#fde047", "#f97316"][i % 3]; }
        else if (kind === "shimmer") {
          bg = ["#e0f2fe", "#bae6fd", "#7dd3fc"][i % 3];
          y = -20 - Math.random() * 24; x = (Math.random() - 0.5) * 18; sz = 2;
        } else if (kind === "fire") {
          bg = ["#f97316", "#fb923c", "#fbbf24", "#fde047"][i % 4];
          y = -18 - Math.random() * 28; x = (Math.random() - 0.5) * 14; sz = 3.5;
        } else if (kind === "confetti") {
          bg = ["#ef4444", "#f97316", "#fbbf24", "#22c55e", "#3b82f6", "#a855f7"][i % 6];
          x = (Math.random() - 0.5) * 60;
          y = 20 + Math.random() * 30; // gravity fall
          sz = 4;
        }
        return (
          <motion.span
            key={i}
            aria-hidden="true"
            initial={{ opacity: 1, x: 0, y: 0, scale: 0.4 }}
            animate={{ opacity: 0, x, y, scale: kind === "shimmer" ? [0.4, 1.4, 0] : [0.4, 1.4, 0.6], rotate: kind === "confetti" ? 360 : 0 }}
            transition={{
              duration: kind === "confetti" ? 1.5 : kind === "fire" ? 1.1 : kind === "shimmer" ? 1.0 : 0.8,
              delay: i * 0.02, ease: "easeOut",
            }}
            className="absolute pointer-events-none rounded-full"
            style={{
              width: sz, height: sz, backgroundColor: bg, left: anchor.left as any, top: anchor.top as any,
              boxShadow: kind === "sparks" ? "0 0 4px 1px rgba(251,191,36,0.6)"
                       : kind === "fire" ? "0 0 6px 2px rgba(251,146,60,0.5)" : undefined,
              transform: "translate(-50%, -50%)",
            }}
          />
        );
      })}
    </>
  );
};

export default AnimatedBrandMark;
